import { useState, useCallback, useRef, useEffect } from 'react';
import { useAPIClient } from '@/hooks/useAPIClient';
import { useToast } from '@/hooks/use-toast';
import {
  StreamingEvent,
  StreamingEventType,
  ToolStartEventData,
  ToolEndEventData,
  CompleteEventData,
  ErrorEventData,
  AgentStartEventData,
  AgentEndEventData,
  ProgressUpdateEventData,
  ChunkEventData,
  ContextTruncatedEventData
} from '@/types/api';

export interface StreamingToolCall {
  tool: string;
  status: 'running' | 'completed' | 'failed';
  args?: Record<string, unknown>;
  result?: string;
  startTime?: number;
  duration?: number;
  resultCount?: number;
}

export interface StreamingState {
  isStreaming: boolean;
  currentResponse: string;
  toolCalls: StreamingToolCall[];
  agentStatus: string;
  isThinking: boolean; // True when agent is processing but no tools are running
  error?: string;
}

interface UseStreamingChatOptions {
  onComplete?: (response: string, sources: string[]) => void;
  onError?: (error: Error) => void;
}

export const useStreamingChat = (sessionId: string, options?: UseStreamingChatOptions) => {
  const apiClient = useAPIClient();
  const { toast } = useToast();
  
  const [state, setState] = useState<StreamingState>({
    isStreaming: false,
    currentResponse: '',
    toolCalls: [],
    agentStatus: 'Ready',
    isThinking: false,
  });

  const streamRef = useRef<{ close: () => void } | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<StreamingEvent> | null>(null);
  const accumulatedResponseRef = useRef<string>('');

  const cleanup = useCallback(() => {
    if (readerRef.current) {
      readerRef.current.cancel();
      readerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.close();
      streamRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const sendMessage = useCallback(async (message: string, attachments?: Array<{data_url: string}>) => {
    try {
      // STEP 1: Reset state to show streaming has started
      accumulatedResponseRef.current = ''; // Reset accumulated response
      setState({
        isStreaming: true,
        currentResponse: '',
        toolCalls: [],
        agentStatus: 'Starting...',
        isThinking: false,
      });

      // STEP 2: Create streaming connection to backend
      // This sends POST request to /api/chat/stream with message, session_id, and optional attachments
      const connection = await apiClient.chat.streamRequest(message, sessionId, attachments);
      streamRef.current = connection;
      
      // STEP 3: Get the ReadableStream reader to consume SSE events
      const reader = connection.stream.getReader();
      readerRef.current = reader;

      let finalSources: string[] = [];
      let finalResponse: string = '';
      
      try {
        // STEP 4: Read streaming events from the SSE connection
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break; // Stream has ended
          
          // STEP 5: Process each streaming event (agent_start, tool_start, tool_end, complete, etc.)
          const event = value as StreamingEvent;
          
          // STEP 6: Update UI state based on event type
          setState(prevState => {
            const newState = { ...prevState };
            
            switch (event.type) {
              case 'agent_start': {
                const data = event.data as AgentStartEventData;
                newState.agentStatus = data.status;
                newState.isThinking = true;
                break;
              }
              
              case 'tool_start': {
                const data = event.data as ToolStartEventData;
                
                // Add new tool call or update existing one
                const existingIndex = newState.toolCalls.findIndex(tc => tc.tool === data.tool);
                const toolCall: StreamingToolCall = {
                  tool: data.tool,
                  status: 'running',
                  args: data.args,
                  startTime: data.timestamp,
                };
                
                if (existingIndex >= 0) {
                  newState.toolCalls[existingIndex] = toolCall;
                } else {
                  newState.toolCalls = [...newState.toolCalls, toolCall];
                }
                
                newState.agentStatus = data.status || 'Running tools...';
                newState.isThinking = false; // Tools are running, not just thinking
                break;
              }
              
              case 'tool_end': {
                const data = event.data as ToolEndEventData;
                
                // Update tool call with results
                newState.toolCalls = newState.toolCalls.map(tc => {
                  if (tc.tool === data.tool) {
                    const duration = tc.startTime ? data.timestamp - tc.startTime : undefined;
                    
                    // Parse result count from result summary
                    let resultCount: number | undefined = undefined;
                    if (data.result_summary) {
                      const resultStr = data.result_summary.toLowerCase();
                      if (resultStr.includes('no results') || resultStr.includes('no documents')) {
                        resultCount = 0;
                      } else if (data.tool === 'combined_search') {
                        // Combined search: parse total_keyword_results and total_semantic_results
                        const keywordMatch = data.result_summary.match(/total_keyword_results['"]\s*:\s*(\d+)/);
                        const semanticMatch = data.result_summary.match(/total_semantic_results['"]\s*:\s*(\d+)/);
                        if (keywordMatch && semanticMatch) {
                          const keywordCount = parseInt(keywordMatch[1], 10);
                          const semanticCount = parseInt(semanticMatch[1], 10);
                          resultCount = keywordCount + semanticCount;
                        }
                      } else {
                        // Try to extract number from result
                        const matches = data.result_summary.match(/(\d+)\s+(?:results?|documents?|matches?)/i);
                        if (matches) {
                          resultCount = parseInt(matches[1], 10);
                        }
                      }
                    }
                    
                    
                    return {
                      ...tc,
                      status: data.success ? 'completed' : 'failed',
                      result: data.result_summary,
                      duration,
                      resultCount,
                    };
                  }
                  return tc;
                });
                
                // Check if any tools are still running
                const stillRunning = newState.toolCalls.some(tc => tc.status === 'running');
                const runningCount = newState.toolCalls.filter(tc => tc.status === 'running').length;
                
                newState.agentStatus = stillRunning ? `Running ${runningCount} tool${runningCount > 1 ? 's' : ''}...` : 'Processing response...';
                newState.isThinking = !stillRunning;
                break;
              }
              
              case 'progress_update': {
                const data = event.data as ProgressUpdateEventData;
                newState.agentStatus = data.message;
                newState.isThinking = true;
                break;
              }
              
              case 'agent_end': {
                const data = event.data as AgentEndEventData;
                newState.agentStatus = data.status;
                newState.isThinking = true; // Agent is thinking/preparing final response
                break;
              }
              
              case 'response_chunk': {
                // If we implement response streaming in the backend later
                const chunk = event.data.chunk as string;
                newState.currentResponse += chunk;
                break;
              }
              
              case 'chunk': {
                // Handle chunk events from backend (new format)
                const data = event.data as ChunkEventData;
                
                if (data.text) {
                  newState.currentResponse += data.text;
                  accumulatedResponseRef.current += data.text;
                  newState.agentStatus = 'Generating response...';
                  newState.isThinking = false;
                }
                break;
              }
              case 'complete': {
                const data = event.data as CompleteEventData;
                
                // Use the accumulated currentResponse if it has content, otherwise use the complete event response
                const responseText = newState.currentResponse.trim() || data.response;
                newState.currentResponse = responseText;
                newState.isStreaming = false;
                newState.agentStatus = 'Complete';
                newState.isThinking = false;
                finalResponse = responseText; // Capture final response (use accumulated or fallback)
                finalSources = data.sources;
                accumulatedResponseRef.current = responseText; // Update ref with final response
                
                // Trigger onComplete callback immediately when we have the complete event
                setTimeout(() => {
                  options?.onComplete?.(responseText, data.sources);
                }, 0);
                
                break;
              }
              
              case 'error': {
                const data = event.data as ErrorEventData;
                newState.error = data.error;
                newState.isStreaming = false;
                newState.agentStatus = 'Error';
                newState.isThinking = false;
                break;
              }
              
              case 'timeout': {
                newState.error = 'Request timed out';
                newState.isStreaming = false;
                newState.agentStatus = 'Timeout';
                newState.isThinking = false;
                break;
              }

              case 'context_truncated': {
                const data = event.data as ContextTruncatedEventData;
                // Display truncation notice like tool calls - appears during stream only
                newState.agentStatus = data.message;
                newState.isThinking = true;
                break;
              }
            }
            
            return newState;
          });
        }
        
        // Stream completed successfully
        
      } catch (streamError) {
        console.error('Streaming error:', streamError);
        
        setState(prevState => ({
          ...prevState,
          isStreaming: false,
          error: streamError instanceof Error ? streamError.message : 'Streaming failed',
          agentStatus: 'Error',
          isThinking: false,
        }));
        
        toast({
          title: "Streaming-Fehler",
          description: streamError instanceof Error ? streamError.message : 'Streaming fehlgeschlagen',
          variant: "destructive",
          duration: 5000,
        });
        
        options?.onError?.(streamError instanceof Error ? streamError : new Error('Streaming failed'));
      }
      
    } catch (error) {
      console.error('Failed to start streaming:', error);
      
      setState(prevState => ({
        ...prevState,
        isStreaming: false,
        error: error instanceof Error ? error.message : 'Failed to start streaming',
        agentStatus: 'Error',
        isThinking: false,
      }));
      
      toast({
        title: "Verbindungsfehler",
        description: error instanceof Error ? error.message : 'Verbindung fehlgeschlagen',
        variant: "destructive",
        duration: 5000,
      });
      
      options?.onError?.(error instanceof Error ? error : new Error('Connection failed'));
    } finally {
      cleanup();
    }
  }, [apiClient, sessionId, options, toast, cleanup]);

  const cancelStreaming = useCallback(() => {
    cleanup();
    setState(prevState => ({
      ...prevState,
      isStreaming: false,
      agentStatus: 'Cancelled',
      isThinking: false,
    }));
  }, [cleanup]);

  return {
    sendMessage,
    cancelStreaming,
    ...state,
  };
};